import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { RegisterSession, CurrentUser, StaffMember, Page } from '../../types';

/**
 * Inline top-bar chip describing the shared register session.
 *
 * Two modes:
 *   - **Owned** (the signed-in user is the cashier who opened the register):
 *     blue badge that reads `Register open • 10:49am`.
 *   - **Sharing** (a different device adopting the cashier's session):
 *     amber badge that reads `Sharing • Fathima • 10:49am`. Tells floor staff
 *     at a glance that this device piggybacks on the cashier's register.
 *
 * Clicking the chip toggles a popover with full session details (cashier,
 * opening float, terminal it was opened on, and an optional "Reports" link).
 *
 * Rendered inline by POSHeader next to the close-register control so the chip
 * sits with the other register UI and doesn't overlap the global Live-data
 * badge in the top-right corner. Renders nothing when there is no active
 * session.
 */

interface SharedRegisterChipProps {
  session: RegisterSession | null | undefined;
  currentUser: CurrentUser | StaffMember | null | undefined;
  /**
   * Lets the popover deep-link to the register session report. Wired up in
   * App.tsx so we don't import the page-router from this leaf component.
   */
  onNavigate?: (page: Page) => void;
}

function formatTime(ms?: number): string {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatDateTime(ms?: number): string {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString([], {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatCurrency(amount: number | undefined): string {
  if (amount == null) return '—';
  try {
    return `$${amount.toFixed(2)}`;
  } catch {
    return `$${amount}`;
  }
}

const SharedRegisterChip: React.FC<SharedRegisterChipProps> = ({ session, currentUser, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Close popover when clicking outside or pressing Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isOwner = useMemo(() => {
    if (!session || !currentUser) return false;
    // Cashier IDs in this codebase are sometimes numeric, sometimes string —
    // compare both as strings to avoid false negatives on shape drift.
    return String((currentUser as any).id) === String(session.cashierId);
  }, [session, currentUser]);

  if (!session || !session.isActive) {
    return null;
  }

  const timeLabel = formatTime(session.startTime);
  const cashierFirstName = (session.cashierName ?? '').split(' ')[0] || 'Cashier';

  const chipBase =
    'inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] sm:text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-1';
  const chipColor = isOwner
    ? 'bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100 focus:ring-blue-400'
    : 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 focus:ring-amber-400';

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${chipBase} ${chipColor}`}
        title={isOwner ? 'Your register is open' : `Sharing the register opened by ${session.cashierName ?? 'another user'}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span
          className={`inline-block w-2 h-2 rounded-full ${
            isOwner ? 'bg-blue-500' : 'bg-amber-500'
          }`}
          aria-hidden
        />
        {isOwner ? (
          <span>Register open · {timeLabel}</span>
        ) : (
          <span>Sharing · {cashierFirstName} · {timeLabel}</span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Shared register session details"
          className="absolute right-0 mt-2 w-72 rounded-lg bg-white shadow-xl border border-gray-200 p-4 text-sm text-gray-700"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">
              {isOwner ? 'Your register session' : 'Shared register session'}
            </h3>
            <span
              className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                isOwner ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-900'
              }`}
            >
              {isOwner ? 'Owner' : 'Sharing'}
            </span>
          </div>
          <dl className="space-y-1.5">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Opened by</dt>
              <dd className="font-medium text-gray-900 text-right truncate">{session.cashierName ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Terminal</dt>
              <dd className="font-medium text-gray-900 text-right truncate">
                {session.terminalName || session.terminalId || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Opening float</dt>
              <dd className="font-medium text-gray-900 text-right">{formatCurrency(session.openingFloat)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Opened at</dt>
              <dd className="font-medium text-gray-900 text-right">{formatDateTime(session.startTime)}</dd>
            </div>
          </dl>

          {!isOwner && (
            <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Sales rung up here will flow to {cashierFirstName}'s register session.
            </p>
          )}

          {onNavigate && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onNavigate('reports-reconciliation');
              }}
              className="mt-3 w-full inline-flex items-center justify-center px-3 py-2 rounded-md text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition"
            >
              View register logs in Reports →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SharedRegisterChip;
